import {LocalParamList} from '../components/tabBar/components/types';
import {getQueryParameterByName} from '../pipes';
import {getDistanceBetweenTwoPoints} from '../pipes';
import {LocalityResponsePayload} from '../models/api';
import {Coordinate} from '../models/localities';
import {getLocalities, setCurrentLocality} from '../../redux/actions/localities';
import {getCurrentPosition} from '../pipes';
import {Platform} from 'react-native';
import {RouteProp} from '@react-navigation/native';
import {storage, StorageKeys} from '../storage';
import {localitiesSlice} from '../../redux/reducers/localities';
import * as React from 'react';
import {
  currentLocalitySelector,
  localitiesListSelector,
  isLocalityDeterminatedSelector,
} from '../../redux/selectors/localities';
import {useSelector} from 'react-redux';
import {useAppDispatch} from '../../redux';
import {defaults} from '../constants/defaults';

let isLocalityDetectionInProgress = false;

export interface WebRoute extends RouteProp<LocalParamList, string> {
  params: {
    city?: string;
  };
}

interface Props {
  route?: WebRoute;
}

export const useLocality = ({route}: Props = {}): LocalityResponsePayload | null => {
  const locality = useSelector(currentLocalitySelector());
  const isDeterminated = useSelector(isLocalityDeterminatedSelector());
  const list = useSelector(localitiesListSelector());
  const dispatch = useAppDispatch();
  const [localityId, setLocalityId] = React.useState<string | null>(null);
  const [userCoordinate, setUserCoordinate] = React.useState<Coordinate | null>(null);

  React.useEffect(() => {
    if (!isDeterminated && !isLocalityDetectionInProgress) {
      isLocalityDetectionInProgress = true;
      (async () => {
        const restoredLocality = await storage.getItem<LocalityResponsePayload>(StorageKeys.locality);
        if (restoredLocality) {
          dispatch(localitiesSlice.actions.setCurrentLocality(restoredLocality));
          isLocalityDetectionInProgress = false;
        } else {
          if (Platform.OS === 'web') {
            const localityIdFromQueryString = String(route?.params?.city) || getQueryParameterByName('city');
            if (localityIdFromQueryString) {
              setLocalityId(localityIdFromQueryString);
              isLocalityDetectionInProgress = false;
            } else {
              const geolocation = navigator.geolocation;
              geolocation.getCurrentPosition(
                (position: GeolocationPosition) => {
                  setUserCoordinate({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                  });
                  isLocalityDetectionInProgress = false;
                },
                () => {
                  dispatch(setCurrentLocality(defaults.locality));
                  isLocalityDetectionInProgress = false;
                },
              );
            }
          } else {
            try {
              const position = await getCurrentPosition();
              setUserCoordinate({
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              });
              isLocalityDetectionInProgress = false;
            } catch {
              dispatch(setCurrentLocality(defaults.locality));
              isLocalityDetectionInProgress = false;
            }
          }
        }
      })();
    }
  }, [isDeterminated]);

  React.useEffect(() => {
    if (!list && (localityId || userCoordinate)) {
      dispatch(getLocalities());
    }
  }, [list, localityId, userCoordinate]);

  React.useEffect(() => {
    if (list && localityId) {
      const foundLocality = list.find((listItem) => listItem.id === +localityId);
      if (foundLocality) {
        dispatch(setCurrentLocality(foundLocality));
      } else {
        dispatch(setCurrentLocality(defaults.locality));
      }
    }
  }, [list, localityId]);

  React.useEffect(() => {
    if (list && userCoordinate) {
      const nearestLocalityData = list.reduce<{distance: number; locality: LocalityResponsePayload | null}>(
        (acc, listItem) => {
          const listItemCoordinate = {
            lat: listItem?.geolocation?.[0] ?? 0,
            lng: listItem?.geolocation?.[1] ?? 0,
          };
          const distance = getDistanceBetweenTwoPoints(userCoordinate, listItemCoordinate);
          if (!acc.distance || distance < acc.distance) {
            acc.distance = distance;
            acc.locality = listItem;
          }
          return acc;
        },
        {distance: 0, locality: null},
      );
      if (nearestLocalityData.locality) {
        dispatch(setCurrentLocality(nearestLocalityData.locality));
      }
    }
  }, [list, userCoordinate]);

  return locality;
};
